/**
 * hiddenDashboards.js
 *
 * Per-browser "Show / hide dashboards" state for the custom-dashboard tab bar. A demo can
 * hide some dashboard tabs in THIS browser only: the hidden page ids live in localStorage
 * under one key per facility. Nothing here touches the dashboards themselves - hidden pages
 * are only left out of the rendered tab bar, and a deep link to one still opens it.
 *
 * Every index here is an index into the FULL `pages` array (the same index space as the
 * component's `tab`, the panels and utils/dashboardTabRoute.js), so hiding a tab can never
 * shift which page an index points at.
 *
 * Reads and writes are total: unavailable storage (private mode), a corrupt value or a
 * non-array payload all read as "nothing hidden", and nothing here throws.
 *
 * PURE module - no Vuex, no components, no other project imports.
 */

// Versioned and namespaced under `ava.dashboard.` alongside dashboardPreferences.js. The
// facility id is appended, so each facility keeps its own hidden list.
export const HIDDEN_DASHBOARDS_KEY_PREFIX = 'ava.dashboard.hiddenTabs.v1.';

/**
 * The localStorage key holding one facility's hidden page ids.
 * @param {string|number} facilityId
 * @returns {string}
 */
export function hiddenDashboardsKey(facilityId) {
  return HIDDEN_DASHBOARDS_KEY_PREFIX + String(facilityId);
}

// Resolve the storage lazily inside the callers' try blocks: merely touching
// `globalThis.localStorage` can throw where storage is blocked.
function resolveStorage(storage) {
  return storage || globalThis.localStorage;
}

/**
 * Read the facility's hidden page ids as strings. A missing facility, unavailable storage,
 * unparseable JSON or a non-array payload all yield []. Total: never throws.
 * @param {string|number} facilityId
 * @param {Storage} [storage] defaults to localStorage
 * @returns {string[]}
 */
export function readHiddenDashboardIds(facilityId, storage) {
  if (facilityId == null || facilityId === '') return [];
  try {
    const raw = resolveStorage(storage).getItem(hiddenDashboardsKey(facilityId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(id => typeof id === 'string' || typeof id === 'number')
      .map(String);
  } catch (err) {
    // storage unavailable or value corrupt - every dashboard visible
    return [];
  }
}

/**
 * Persist the facility's hidden page ids. An empty list removes the key. Returns false when
 * storage is unavailable or full so the caller can tell the user the choice will not survive
 * a reload. Total: never throws.
 * @param {string|number} facilityId
 * @param {Array<string|number>} ids
 * @param {Storage} [storage] defaults to localStorage
 * @returns {boolean}
 */
export function writeHiddenDashboardIds(facilityId, ids, storage) {
  if (facilityId == null || facilityId === '') return false;
  try {
    const target = resolveStorage(storage);
    const key = hiddenDashboardsKey(facilityId);
    const list = (ids || []).map(String);
    if (list.length) target.setItem(key, JSON.stringify(list));
    else target.removeItem(key);
    return true;
  } catch (err) {
    return false;
  }
}

/**
 * A new hidden-id list with `pageId` hidden (`visible` false) or shown (`visible` true).
 * Never mutates `hiddenIds`.
 * @param {Array<string|number>} hiddenIds
 * @param {string|number} pageId
 * @param {boolean} visible
 * @returns {string[]}
 */
export function withDashboardVisibility(hiddenIds, pageId, visible) {
  const id = String(pageId);
  const rest = (hiddenIds || []).map(String).filter(h => h !== id);
  return visible ? rest : [...rest, id];
}

/**
 * The hidden ids that actually apply to `pages`: stored ids that still match a current page
 * (stale ids of deleted pages drop out), or [] when they would hide every page (e.g. the
 * last visible page was deleted) - the tab bar is never empty. This is the single rule every
 * other helper and the dialog build on, so a switch always shows what the tab bar does.
 * @param {Array<{id: string|number}>} pages
 * @param {Array<string|number>} hiddenIds
 * @returns {string[]}
 */
export function effectiveHiddenIds(pages, hiddenIds) {
  const pageIds = (pages || []).filter(Boolean).map(page => String(page.id));
  const current = new Set(pageIds);
  const kept = [...new Set((hiddenIds || []).map(String))].filter(id => current.has(id));
  const keptSet = new Set(kept);
  if (pageIds.every(id => keptSet.has(id))) return [];
  return kept;
}

/**
 * Full-`pages` indices of the pages left visible by effectiveHiddenIds.
 * @param {Array<{id: string|number}>} pages
 * @param {Array<string|number>} hiddenIds
 * @returns {number[]}
 */
export function visiblePageIndices(pages, hiddenIds) {
  const list = pages || [];
  const hidden = new Set(effectiveHiddenIds(list, hiddenIds));
  const visible = [];
  list.forEach((page, index) => {
    if (!page || !hidden.has(String(page.id))) visible.push(index);
  });
  return visible;
}

/**
 * The tab entries to render: every visible page plus the ACTIVE page even when hidden. The
 * active page must stay rendered - it is how a deep link to a hidden dashboard stays open,
 * and the mandatory tab toggle would otherwise select a different button than `tab`.
 * @param {Array<{id: string|number}>} pages
 * @param {Array<string|number>} hiddenIds
 * @param {number} activeIndex
 * @returns {Array<{page: object, index: number}>}
 */
export function renderedTabEntries(pages, hiddenIds, activeIndex) {
  const list = pages || [];
  const visible = new Set(visiblePageIndices(list, hiddenIds));
  const entries = [];
  list.forEach((page, index) => {
    if (visible.has(index) || index === activeIndex) entries.push({ page, index });
  });
  return entries;
}

/**
 * Prefer a visible tab for a fallback landing (no / unknown ?tab=, deletion): `index` when
 * it is visible, else the first visible page. Explicit deep links to a hidden page do NOT go
 * through this - they still open it. -1 stays -1.
 * @param {Array<{id: string|number}>} pages
 * @param {Array<string|number>} hiddenIds
 * @param {number} index
 * @returns {number}
 */
export function preferVisibleTabIndex(pages, hiddenIds, index) {
  if (index < 0) return index;
  const visible = visiblePageIndices(pages, hiddenIds);
  if (visible.includes(index)) return index;
  return visible.length ? visible[0] : index;
}

export default {
  HIDDEN_DASHBOARDS_KEY_PREFIX,
  hiddenDashboardsKey,
  readHiddenDashboardIds,
  writeHiddenDashboardIds,
  withDashboardVisibility,
  effectiveHiddenIds,
  visiblePageIndices,
  renderedTabEntries,
  preferVisibleTabIndex,
};
